// Core
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Libraries
import {
  isChrome,
  isFirefox,
  browserName,
  isEdge,
} from 'react-device-detect';
import moment from 'moment';
import 'moment/locale/es';

// Plugins
import {
  configureAws,
  useInterval,
} from '../plugins/general';
import { requestVantageApi } from '../plugins/request';
import { langMap, fallbackLanguage } from '../i18n/i18n';

// Notifications
import { getSupportedLevels } from '../views/CustomerPanel/Notifications/notificationLevelKeys';

// Layout
import Message from '../layout/elements/Message';

// Store
import {
  setAlarms,
  setAlerts,
  setApplication,
  setLanguage,
  setNotificationLevels,
  setUnitSystem,
} from '../store/app/actions';
import { mergeUserDetails } from '../store/auth/actions';
import { refreshCurrentCustomerEntitlements } from '../store/session/actions';

const PREFERENCES_SYNC_DEBOUNCE_MS = 600;
const ENTITLEMENTS_POLL_INTERVAL_MS = 60000;

const normalizeNotificationLevels = (levels) => [...(levels ?? [])].sort();

const buildPreferencesSnapshot = ({
  lang,
  alarms,
  alerts,
  notificationLevels,
}) => ({
  lang,
  alarms,
  alerts,
  notificationLevels: notificationLevels ?? [],
});

export const buildServerPreferences = (customerProfile, fallbackLang) => (
  buildPreferencesSnapshot({
    lang: customerProfile.lang || fallbackLang,
    alarms: typeof customerProfile.alarms === 'boolean' ? customerProfile.alarms : false,
    alerts: typeof customerProfile.alerts === 'boolean' ? customerProfile.alerts : false,
    notificationLevels: Array.isArray(customerProfile.notificationLevels)
      ? getSupportedLevels(customerProfile.notificationLevels)
      : [],
  })
);

const areNotificationLevelsEqual = (left, right) => (
  JSON.stringify(normalizeNotificationLevels(left))
  === JSON.stringify(normalizeNotificationLevels(right))
);

const arePreferencesEqual = (left, right, ignoreLanguage = false) => (
  (ignoreLanguage || left.lang === right.lang)
  && left.alarms === right.alarms
  && left.alerts === right.alerts
  && areNotificationLevelsEqual(left.notificationLevels, right.notificationLevels)
);

export const AppController = (props) => {
  const {
    unitSystem,
    language,
    alarms,
    alerts,
    notificationLevels,
    setApplicationAction,
    setUnitSystemAction,
    setLanguageAction,
    setAlarmsAction,
    setAlertsAction,
    setNotificationLevelsAction,
    mergeUserDetailsAction,
    refreshCustomerEntitlementsAction,
    children,
    user,
    userDetails,
    customer,
  } = props;

  /* ***********************************************************************************************
   ********************************************** DEPENDENCIES *************************************
   ********************************************************************************************** */

  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get('lang');
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isChromeHeadless = browserName === 'Chrome Headless';

  /* ***********************************************************************************************
   ********************************************** LOCAL STATE *************************************
   ********************************************************************************************** */

  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [lastUrl, setLastUrl] = useState(null);
  const preferencesHydratedRef = useRef(false);
  const syncedPreferencesRef = useRef(null);
  const hydratedProfileIdRef = useRef(null);
  const isSyncingPreferencesRef = useRef(false);
  const queuedPreferencesRef = useRef(null);
  const urlLanguageRef = useRef(null);

  const currentPreferences = useMemo(
    () => buildPreferencesSnapshot({
      lang: language,
      alarms,
      alerts,
      notificationLevels,
    }),
    [language, alarms, alerts, notificationLevels],
  );

  /* ***********************************************************************************************
   ********************************************** METHODS *******************************************
   ********************************************************************************************** */

  const fetchAppInformation = () => {
    requestVantageApi.application('get', `/${process.env.REACT_APP_ID}`, null, true, false)
      .then((response) => {
        setApplicationAction(response.data);
        setMaintenanceMode(response.data.maintenanceMode);
      });
  };

  const setAppLanguage = () => {
    let selectedLanguage;
    let isLanguageFromUrl = false;

    if (urlLang) {
      selectedLanguage = urlLang;
      isLanguageFromUrl = true;
    } else if (user && user.mgnt && user.mgnt.lang) {
      selectedLanguage = user.mgnt.lang;
    } else if (language) {
      selectedLanguage = language;
    } else {
      const browserLanguages = [];

      // Get browser langs
      if (navigator.language) browserLanguages.push(navigator.language);
      if (navigator.languages && navigator.languages.length) {
        navigator.languages.forEach((lang) => browserLanguages.push(lang));
      }

      // Verify lang by lang & store first valid
      if (browserLanguages.length) {
        for (let i = 0; i < browserLanguages.length; i += 1) {
          const lang = browserLanguages[i];
          if (lang) {
            const normalizedLang = browserLanguages[i].replace('-', '_').toLowerCase();
            if (langMap[normalizedLang]) {
              selectedLanguage = langMap[normalizedLang];
              break;
            }
          }
        }
      }
    }

    if (selectedLanguage) {
      moment.locale(selectedLanguage);
      i18n.changeLanguage(selectedLanguage);

      if (selectedLanguage !== language) {
        setLanguageAction(selectedLanguage);
      }

      if (isLanguageFromUrl) urlLanguageRef.current = selectedLanguage;
    } else {
      // keeps fallback lang
      moment.locale(fallbackLanguage);
    }
  };

  const setAppUnitSystem = () => {
    if (!unitSystem) {
      if (language === 'en_US') setUnitSystemAction('imperial');
      else setUnitSystemAction('metric');
    }
  };

  const getCustomerNotificationProfile = () => {
    if (!user.sub || user['custom:isSuperior'] !== '0') {
      return null;
    }

    if (userDetails?.idUser === user.sub) {
      return userDetails;
    }

    return null;
  };

  const applyPreferencesToStore = (preferences) => {
    if (preferences.lang) {
      moment.locale(preferences.lang);
      i18n.changeLanguage(preferences.lang);

      if (preferences.lang !== language) {
        setLanguageAction(preferences.lang);
      }
    }
    if (preferences.alarms !== alarms) {
      setAlarmsAction(preferences.alarms);
    }
    if (preferences.alerts !== alerts) {
      setAlertsAction(preferences.alerts);
    }
    if (!areNotificationLevelsEqual(preferences.notificationLevels, notificationLevels)) {
      setNotificationLevelsAction(preferences.notificationLevels);
    }
  };

  const sendUserPreferencesToApi = (preferences, includeLanguage) => {
    const body = {
      alarms: preferences.alarms,
      alerts: preferences.alerts,
      notificationLevels: preferences.notificationLevels ?? [],
    };

    // Omitted when the language came from the URL, so a shared link never
    // rewrites the language stored in the profile
    if (includeLanguage) body.lang = preferences.lang;

    return requestVantageApi.child(customer.api.user, 'put', `/${user.sub}`, body, false);
  };

  const flushPreferencesSync = async (preferences) => {
    const syncedPreferences = syncedPreferencesRef.current;
    if (!syncedPreferences) return;

    const includeLanguage = urlLanguageRef.current !== preferences.lang;
    if (arePreferencesEqual(preferences, syncedPreferences, !includeLanguage)) return;

    // Requests are serialized so the stored value reflects the last intent,
    // not whichever response happens to arrive last
    if (isSyncingPreferencesRef.current) {
      queuedPreferencesRef.current = preferences;
      return;
    }

    isSyncingPreferencesRef.current = true;

    try {
      await sendUserPreferencesToApi(preferences, includeLanguage);
      syncedPreferencesRef.current = preferences;
      mergeUserDetailsAction({
        userDetails: {
          alarms: preferences.alarms,
          alerts: preferences.alerts,
          notificationLevels: preferences.notificationLevels,
          ...(includeLanguage ? { lang: preferences.lang } : {}),
        },
      });
    } catch {
      queuedPreferencesRef.current = null;
      applyPreferencesToStore(syncedPreferences);
      Message.error(t('view.cp.notifications.message.preferencesError'));
    }

    isSyncingPreferencesRef.current = false;

    const queuedPreferences = queuedPreferencesRef.current;
    queuedPreferencesRef.current = null;
    if (queuedPreferences) flushPreferencesSync(queuedPreferences);
  };

  /* ***********************************************************************************************
   ***************************************** COMPONENT HANDLING ************************************
   ********************************************************************************************** */

  useEffect(() => {
    configureAws();
    fetchAppInformation();
    setAppUnitSystem();
  }, []);

  useEffect(() => {
    setAppLanguage();
  }, [user]);

  useEffect(() => {
    if (!user.sub) {
      preferencesHydratedRef.current = false;
      syncedPreferencesRef.current = null;
      hydratedProfileIdRef.current = null;
      queuedPreferencesRef.current = null;
    }
  }, [user.sub]);

  useEffect(() => {
    if (!user.sub || user['custom:isSuperior'] !== '0') {
      preferencesHydratedRef.current = false;
      syncedPreferencesRef.current = null;
      hydratedProfileIdRef.current = null;
      return undefined;
    }

    const customerProfile = getCustomerNotificationProfile();
    if (!customerProfile?.idUser) {
      preferencesHydratedRef.current = false;
      syncedPreferencesRef.current = null;
      return undefined;
    }

    // Hydrate once per customer profile load — not on every local PUT sync
    if (
      preferencesHydratedRef.current
      && hydratedProfileIdRef.current === customerProfile.idUser
    ) {
      return undefined;
    }

    // Built from the exact values pushed to the store, so hydration by itself
    // never looks like a local change that has to be sent back
    const serverPreferences = buildServerPreferences(customerProfile, language);

    applyPreferencesToStore(serverPreferences);
    syncedPreferencesRef.current = serverPreferences;
    preferencesHydratedRef.current = true;
    hydratedProfileIdRef.current = customerProfile.idUser;

    return undefined;
  }, [
    user.sub,
    userDetails?.idUser,
  ]);

  useEffect(() => {
    if (
      !preferencesHydratedRef.current
      || !user.sub
      || user['custom:isSuperior'] !== '0'
      || !customer?.api?.user
      || !syncedPreferencesRef.current
    ) {
      return undefined;
    }

    // Debounced so a burst of preference changes collapses into a single request
    const timeoutId = window.setTimeout(
      () => flushPreferencesSync(currentPreferences),
      PREFERENCES_SYNC_DEBOUNCE_MS,
    );

    return () => window.clearTimeout(timeoutId);
  }, [
    currentPreferences,
    customer?.api?.user,
    user.sub,
  ]);

  useInterval(() => {
    fetchAppInformation();
  }, 300000);

  // Soft-refresh entitlements while a customer session is active.
  // Skips hidden tabs; Redux only updates when flags actually change.
  useInterval(() => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    if (!customer?.api?.user) {
      return;
    }

    refreshCustomerEntitlementsAction();
  }, ENTITLEMENTS_POLL_INTERVAL_MS);

  useEffect(() => {
    const refreshEntitlementsIfVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      if (!customer?.api?.user) {
        return;
      }

      refreshCustomerEntitlementsAction();
    };

    document.addEventListener('visibilitychange', refreshEntitlementsIfVisible);
    window.addEventListener('focus', refreshEntitlementsIfVisible);

    return () => {
      document.removeEventListener('visibilitychange', refreshEntitlementsIfVisible);
      window.removeEventListener('focus', refreshEntitlementsIfVisible);
    };
  }, [customer, refreshCustomerEntitlementsAction]);

  if (!isChrome && !isFirefox && !isChromeHeadless && !isEdge) {
    history.push('/unsupported-browser');
  } else if (maintenanceMode) {
    if (!history.location.pathname.includes('/maintenance')) {
      setLastUrl(history.location.pathname);
      history.push('/maintenance');
    }
  } else if (!maintenanceMode) {
    if (history.location.pathname.includes('/maintenance')) {
      window.location.href = lastUrl;
    }
  }

  return (
    <>
      {children}
    </>
  );
};

AppController.propTypes = {
  unitSystem: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  alarms: PropTypes.bool.isRequired,
  alerts: PropTypes.bool.isRequired,
  notificationLevels: PropTypes.arrayOf(PropTypes.string).isRequired,
  user: PropTypes.object.isRequired,
  userDetails: PropTypes.object.isRequired,
  customer: PropTypes.object,
  setApplicationAction: PropTypes.func.isRequired,
  setUnitSystemAction: PropTypes.func.isRequired,
  setLanguageAction: PropTypes.func.isRequired,
  setAlarmsAction: PropTypes.func.isRequired,
  setAlertsAction: PropTypes.func.isRequired,
  setNotificationLevelsAction: PropTypes.func.isRequired,
  mergeUserDetailsAction: PropTypes.func.isRequired,
  refreshCustomerEntitlementsAction: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

AppController.defaultProps = {
  customer: null,
};

// Map state
const mapStateToProps = (state) => ({
  unitSystem: state.app.unitSystem,
  language: state.app.language,
  alarms: state.app.alarms,
  alerts: state.app.alerts,
  notificationLevels: state.app.notificationLevels,
  user: state.auth.user,
  userDetails: state.auth.userDetails,
  customer: state.session.customer,
});

const mapDispatchToProps = (dispatch) => ({
  setApplicationAction: (app) => dispatch(setApplication(app)),
  setUnitSystemAction: (unitSystem) => dispatch(setUnitSystem(unitSystem)),
  setLanguageAction: (language) => dispatch(setLanguage(language)),
  setAlarmsAction: (enabled) => dispatch(setAlarms(enabled)),
  setAlertsAction: (enabled) => dispatch(setAlerts(enabled)),
  setNotificationLevelsAction: (levels) => dispatch(setNotificationLevels(levels)),
  mergeUserDetailsAction: (payload) => dispatch(mergeUserDetails(payload)),
  refreshCustomerEntitlementsAction: () => dispatch(refreshCurrentCustomerEntitlements()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppController);
